import { useState, useCallback, useEffect, memo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, Upload, Shuffle, User, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useUser } from "@/contexts/UserContext";
import { uploadsService } from "@/services/uploadsService";

interface ProfilePictureStepProps {
  onSkip?: () => void;
  onBack?: () => void;
}

const ProfilePictureStep = memo(({ onSkip, onBack }: ProfilePictureStepProps) => {
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { user, updateUserProfile } = useUser();
  

  // Initialize avatar URL from user profile or sessionStorage
  useEffect(() => {
    // First check sessionStorage for persisted avatar during onboarding
    const savedAvatar = sessionStorage.getItem('onboarding-avatar-url');
    if (savedAvatar) {
      setAvatarUrl(savedAvatar);
    } else if (user?.avatarUrl && !avatarUrl) {
      setAvatarUrl(user.avatarUrl);
    }
  }, []);

  // Force re-sync when user profile updates (but not during onboarding or upload)
  useEffect(() => {
    // Don't update if we're currently uploading or have a local avatar
    if (isUploading || isSaving) return;
    
    const savedAvatar = sessionStorage.getItem('onboarding-avatar-url');
    if (!savedAvatar && user?.avatarUrl && user.avatarUrl !== avatarUrl) {
      setAvatarUrl(user.avatarUrl);
    }
  }, [user?.avatarUrl, isUploading, isSaving, avatarUrl]);

  // Generate random avatar using DiceBear (via backend)
  const generateRandomAvatar = useCallback(async () => {
    setIsGenerating(true);
    try {
      // uploadsService returns response.data.data directly (already unwrapped by apiService)
      const response = await uploadsService.generateAvatar('svg');
      const randomAvatarUrl = response?.avatar_url || response?.avatarUrl;
      
      if (!randomAvatarUrl) {
        throw new Error('No avatar URL returned from server');
      }
      
      setAvatarUrl(randomAvatarUrl);
      // Save to sessionStorage to persist across re-renders
      sessionStorage.setItem('onboarding-avatar-url', randomAvatarUrl);
      
      // Update user profile immediately so avatar shows in header
      if (user) {
        try {
          await updateUserProfile({ avatarUrl: randomAvatarUrl });
        } catch (error) {
          console.warn('Failed to update user profile with avatar:', error);
          // Continue anyway since sessionStorage is set
        }
      }
      
      toast({
        title: "Avatar generated!",
        description: "Your profile picture has been updated.",
      });
    } catch (error) {
      console.error('Error generating avatar:', error);
      toast({
        title: "Generation failed",
        description: "Failed to generate avatar. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  }, [user, updateUserProfile, toast]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent any default behavior that might cause page reload
    event.preventDefault();
    
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file (PNG, JPG, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (20MB max to match bucket settings)
    if (file.size > 20 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 20MB",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      // Upload avatar via uploadsService
      const response = await uploadsService.uploadAvatar(file);

      if (!response || !response.url) {
        throw new Error('Upload succeeded but no URL returned');
      }

      // Set the avatar URL immediately for UI update
      setAvatarUrl(response.url);
      
      // Save to sessionStorage to persist across re-renders
      sessionStorage.setItem('onboarding-avatar-url', response.url);
      
      // Update user profile immediately so avatar shows in header
      if (user) {
        try {
          await updateUserProfile({ avatarUrl: response.url });
        } catch (error) {
          console.warn('Failed to update user profile with avatar:', error);
          // Continue anyway since sessionStorage is set
        }
      }
      
      toast({
        title: "Photo uploaded successfully!",
        description: "Your profile picture has been updated.",
      });

      // Small delay to ensure upload is fully processed
      await new Promise(resolve => setTimeout(resolve, 1000));

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      // Reset file input to allow retry
      const fileInput = document.getElementById('photo-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } finally {
      setIsUploading(false);
    }
  }, [user?.id, toast, updateUserProfile]);

  // Save avatar and continue
  const handleSaveAndContinue = async () => {
    if (avatarUrl && user) {
      setIsSaving(true);
      try {
        // Store the avatar URL in sessionStorage to persist across re-renders
        sessionStorage.setItem('onboarding-avatar-url', avatarUrl);
        
        // Instead of updating profile immediately, store for batch update at onboarding completion
        // This prevents re-rendering during the onboarding flow
        
        toast({
          title: "Profile picture ready!",
          description: "Your photo will be saved when you complete onboarding.",
        });
        
        // Advance to next step without triggering profile update
        onSkip?.();
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        toast({
          title: "Save failed",
          description: `Failed to prepare profile picture: ${errorMessage}`,
          variant: "destructive",
        });
        // Don't advance to next step on error - stay on current step
      } finally {
        setIsSaving(false);
      }
    } else {
      // If no avatar selected, just continue
      onSkip?.();
    }
  };

  const currentAvatar = avatarUrl || user?.avatarUrl || "";

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="mb-4">
          <Avatar className="h-24 w-24 mx-auto border-4 border-white shadow-lg">
            <AvatarImage src={currentAvatar} alt="Profile picture" />
            <AvatarFallback className="text-2xl bg-gradient-to-br from-primary/20 to-primary/40">
              <User className="h-8 w-8" />
            </AvatarFallback>
          </Avatar>
        </div>
        <p className="text-sm text-muted-foreground">
          Add a profile picture to personalize your account and help teammates recognize you
        </p>
      </div>

      <div className="grid gap-3">
        {/* Upload Photo */}
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <label htmlFor="photo-upload" className="cursor-pointer block">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  {isUploading ? (
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                  ) : (
                    <Upload className="h-5 w-5 text-blue-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm">Upload a photo</h3>
                  <p className="text-xs text-muted-foreground">
                    {isUploading ? 'Uploading your photo...' : 'Choose a photo from your device (PNG, JPG, max 5MB)'}
                  </p>
                </div>
                <Camera 
                  className="h-4 w-4 text-muted-foreground cursor-pointer hover:text-primary transition-colors" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.preventDefault();
                    document.getElementById('photo-upload')?.click();
                  }}
                />
              </div>
            </label>
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              disabled={isUploading}
            />
          </CardContent>
        </Card>

        {/* Generate Random Avatar */}
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                generateRandomAvatar();
              }}
              disabled={isGenerating}
              className="w-full text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-full">
                  {isGenerating ? (
                    <Loader2 className="h-5 w-5 text-purple-600 animate-spin" />
                  ) : (
                    <Shuffle className="h-5 w-5 text-purple-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm">Generate avatar</h3>
                  <p className="text-xs text-muted-foreground">
                    {isGenerating ? 'Generating your avatar...' : 'Create a fun, randomized avatar illustration'}
                  </p>
                </div>
              </div>
            </button>
          </CardContent>
        </Card>
      </div>

      <div className="text-center space-y-3">
        {avatarUrl && (
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleSaveAndContinue();
            }}
            className="w-full"
            disabled={isUploading || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save and Continue"
            )}
          </Button>
        )}
        
        <div className="flex gap-2 justify-center">
          {onBack && (
            <Button 
              type="button"
              variant="outline" 
              onClick={(e) => {
                e.preventDefault();
                onBack();
              }}
              className="text-sm"
              disabled={isUploading || isSaving}
            >
              Back
            </Button>
          )}
          <Button 
            type="button"
            variant="ghost" 
            onClick={(e) => {
              e.preventDefault();
              onSkip?.();
            }}
            className="text-sm"
            disabled={isUploading || isSaving}
          >
            {avatarUrl ? 'Continue without saving' : 'Skip for now'}
          </Button>
        </div>
        
        <p className="text-xs text-muted-foreground">
          You can always update your profile picture later in your profile settings
        </p>
      </div>
    </div>
  );
});

ProfilePictureStep.displayName = 'ProfilePictureStep';

export default ProfilePictureStep;